import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { WORKFLOW_INITIAL_OBJECT } from 'pages/DesignOrchestration/DesignOrchestrationDiagram/DesignOrchestrationDiagram.constans';
import {
  updateWorkflowState,
  assignStepIndexes,
  extractStepIndexByUniqueId,
} from 'pages/DesignOrchestration/DesignOrchestrationDiagram/utils/workflowHelper/designOrchestrationWorkflowHelpers';
import {
  dropZoneStepTypes,
  IDropZoneWorkflowStep,
} from 'interfaces/designOrchestration/designOrchestrationDiagram.interface';
import {
  IModuleOnDrag,
  IStepModule,
} from 'interfaces/designOrchestration/designOrchestrationModules.interface';
import { IDesignOrchestrationSlice } from 'interfaces/designOrchestration/designOrchestrationState.interface';
import { VIEW_OPTIONS } from 'pages/DesignOrchestration/DesignOrchestration.constants';
import { RootState } from 'redux/store';
import { includePropertiesInCleanStep } from 'utils/commonFunctions/CommonFunctions';
import {
  removeModulesFromWorkflow,
  findStepInWorkflow,
  findAllRelatedStepIds,
  filterStepModulesForDeletion,
} from 'pages/DesignOrchestration/DesignOrchestrationDiagram/utils/deleteHelpers/designOrchestrationFunctions';
import { Useuid } from 'utils/hooks/useUid';

export const SLICE_KEY = 'designOrchestration';

const initialState: IDesignOrchestrationSlice = {
  deleteTooltipOpenId: '',
  isAccordionOpen: true,
  orchestrationDetails: {
    businessProduct: undefined,
    cronTimezone: undefined,
    cronTrigger: undefined,
    eventParamsMapping: undefined,
    ftpDropzone: undefined,
    notificationEmails: undefined,
    orchestrationDescription: undefined,
    orchestrationName: undefined,
    reportingIndex: undefined,
    s3Dropzone: undefined,
    sftpDropzone: undefined,
    sftpPrivateKey: undefined,
    throttleRate: undefined,
    unpackProduct: undefined,
  },
  selectedElementId: null,
  selectedElementIdToDelete: null,
  selectedElementIds: [],
  selectedElementIdsToDelete: [],
  stepModules: [],
  viewState: {
    currentView: VIEW_OPTIONS.COLUMN_VIEW,
    isDetailsOpen: true,
    isModulesOpen: true,
  },
  workflowState: WORKFLOW_INITIAL_OBJECT,
  xmlCode: '',
  xmlPanelWidth: 0,
};

const designOrchestrationSlice = createSlice({
  initialState,
  name: SLICE_KEY,
  reducers: {
    addStepModuleAndUpdateWorkflow: (
      state,
      action: PayloadAction<{
        existingNodeIndex?: number;
        isGlobalFailback: boolean;
        module: IModuleOnDrag;
        moduleVersion: string;
        stepType: dropZoneStepTypes | undefined;
        targetWorkflowStepId: string;
        textId: string;
      }>
    ) => {
      try {
        // Step 1: Use module data from payload
        const moduleData = action.payload.module;

        if (!moduleData || !moduleData.parameters) {
          console.error(
            `addStepModuleAndUpdateWorkflow: Invalid module data. textId: ${action.payload.textId}, version: ${action.payload.moduleVersion}`
          );
          return;
        }

        // Step 2: Generate uniqueId and prepare module with uniqueId
        const uniqueId = Useuid();
        const moduleWithUniqueId = {
          ...moduleData,
          uniqueId,
        };

        // Step 3: Attempt to update the workflow state
        const newWorkflowState = updateWorkflowState(
          action.payload?.targetWorkflowStepId,
          state.workflowState,
          moduleWithUniqueId,
          action.payload?.isGlobalFailback
        );

        // Step 4: Verify that the workflow was actually updated
        if (newWorkflowState === state.workflowState) {
          console.error(
            `addStepModuleAndUpdateWorkflow: Workflow update failed. targetWorkflowStepId: ${action.payload.targetWorkflowStepId}, isGlobalFailback: ${action.payload.isGlobalFailback}`
          );
          return;
        }

        // Step 5: Assign step indexes to the updated workflow
        const workflowWithIndexes = assignStepIndexes(newWorkflowState);

        // Step 6: Extract step index mapping from the updated workflow
        const stepIndexMap = extractStepIndexByUniqueId(workflowWithIndexes);

        // Step 7: Verify that our new step exists in the workflow with a valid stepIndex
        if (stepIndexMap[uniqueId] === undefined) {
          console.error(
            `addStepModuleAndUpdateWorkflow: Step not found in updated workflow. uniqueId: ${uniqueId}, stepIndexMap:`,
            stepIndexMap
          );
          return;
        }

        // Step 8: Prepare the new step module with sorted parameters
        const sortedParameters = [...moduleData.parameters]?.sort((a, b) => {
          const aQualified =
            a.type === 'ENUM' &&
            moduleData.parameters?.some(
              (parameter) => parameter.dependsId === a.textId
            );
          const bQualified =
            b.type === 'ENUM' &&
            moduleData.parameters?.some(
              (parameter) => parameter.dependsId === b.textId
            );

          if (aQualified && !bQualified) {
            return -1;
          }
          if (!aQualified && bQualified) {
            return 1;
          }
          return 0;
        });

        const filteredStepModules = action.payload.existingNodeIndex
          ? state.stepModules.filter(
              ({ stepIndex }) => stepIndex !== action.payload.existingNodeIndex
            )
          : state.stepModules;
        const newStepModule: IStepModule = {
          ...moduleData,
          isOpen: true,
          order: filteredStepModules.length + 1,
          parameters: sortedParameters,
          stepIndex: stepIndexMap[uniqueId],
          stepType: action.payload.stepType,
          uniqueId,
          version: action.payload.moduleVersion,
        } as unknown as IStepModule;

        // Step 9: Update existing stepModules with new stepIndexes
        const updatedExistingStepModules = filteredStepModules.map(
          (stepModule) => {
            if (
              stepModule.uniqueId &&
              stepIndexMap[stepModule.uniqueId] !== undefined
            ) {
              return {
                ...stepModule,
                stepIndex: stepIndexMap[stepModule.uniqueId],
              };
            }
            return stepModule;
          }
        );

        // Step 10: Combine existing and new step modules
        const finalStepModules = [...updatedExistingStepModules, newStepModule];

        // Step 11: Return the updated state with both workflow and stepModules synchronized
        state.workflowState = workflowWithIndexes;
        state.stepModules = finalStepModules;
      } catch (error) {
        console.error(
          'addStepModuleAndUpdateWorkflow: Unexpected error occurred:',
          error,
          {
            currentStepModulesLength: state.stepModules.length,
            currentWorkflowState: state.workflowState,
            payload: action.payload,
          }
        );
        // Don't modify state on error
      }
    },
    clearSelectedElements: (state) => {
      state.selectedElementId = null;
      state.selectedElementIds = [];
      state.selectedElementIdToDelete = null;
      state.selectedElementIdsToDelete = [];
    },
    clearState: () => {
      return initialState;
    },
    deleteStepModuleAndUpdateWorkflow: (
      state,
      action: PayloadAction<string>
    ) => {
      try {
        const stepId = action.payload;

        // Find the step in workflow to get its stepIndex and uniqueId
        let targetStep: IDropZoneWorkflowStep | null = null;

        // Search in main workflow
        if (state.workflowState.workflow.nextStepGroup) {
          targetStep = findStepInWorkflow(
            state.workflowState.workflow.nextStepGroup,
            stepId
          );
        }

        // Search in failback workflow if not found in main
        if (!targetStep && state.workflowState.workflow.failbackStepGroup) {
          targetStep = findStepInWorkflow(
            state.workflowState.workflow.failbackStepGroup,
            stepId
          );
        }

        // Step 1: Find all related step IDs that need to be deleted
        const [relatedStepIds, relatedUniqueIds] = findAllRelatedStepIds(
          stepId,
          state.workflowState
        );

        // Step 2: Remove modules from stepModules array
        const updatedStepModules = filterStepModulesForDeletion(
          state.stepModules,
          targetStep,
          relatedStepIds,
          relatedUniqueIds
        );

        // Step 3: Update workflow state by removing the modules
        const newWorkflowState = removeModulesFromWorkflow(
          state.workflowState,
          relatedStepIds
        );

        // Step 4: Reassign step indexes after deletion
        const workflowWithIndexes = assignStepIndexes(newWorkflowState);

        // Step 5: Extract updated step index mapping
        const stepIndexMap = extractStepIndexByUniqueId(workflowWithIndexes);

        // Step 6: Update remaining stepModules with new stepIndexes and renumber orders
        const finalStepModules = updatedStepModules
          .map((stepModule) => {
            if (
              stepModule.uniqueId &&
              stepIndexMap[stepModule.uniqueId] !== undefined
            ) {
              return {
                ...stepModule,
                stepIndex: stepIndexMap[stepModule.uniqueId],
              };
            }
            return stepModule;
          })
          .map((stepModule, index) => ({
            ...stepModule,
            order: index + 1,
          }));

        return {
          ...state,
          selectedElementId: null,
          selectedElementIds: [],
          stepModules: finalStepModules,
          workflowState: workflowWithIndexes,
        };
      } catch (error) {
        console.error(
          'deleteStepModuleAndUpdateWorkflow: Unexpected error occurred:',
          error
        );
        return state;
      }
    },
    draggingStepModuleAndUpdateWorkflow: (
      state,
      action: PayloadAction<string>
    ) => {
      try {
        const stepId = action.payload;

        const [relatedStepIds] = findAllRelatedStepIds(
          stepId,
          state.workflowState
        );

        const newWorkflowState = removeModulesFromWorkflow(
          state.workflowState,
          relatedStepIds
        );

        return {
          ...state,
          selectedElementId: null,
          selectedElementIds: [],
          stepModules: state.stepModules,
          workflowState: newWorkflowState,
        };
      } catch (error) {
        console.error(
          'deleteStepModuleAndUpdateWorkflow: Unexpected error occurred:',
          error
        );
        return state;
      }
    },
    setAccordionStatus: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isAccordionOpen: action.payload,
      };
    },
    setIsDeleteTooltipOpen: (state, action: PayloadAction<string>) => {
      state.deleteTooltipOpenId = action.payload;
    },
    setOrchestrationDetails: (
      state,
      action: PayloadAction<
        Partial<IDesignOrchestrationSlice['orchestrationDetails']>
      >
    ) => {
      state.orchestrationDetails = {
        ...state.orchestrationDetails,
        ...action.payload,
      };
    },
    setSelectedElement: (state, action: PayloadAction<string | null>) => {
      state.selectedElementId = action.payload;
    },
    setSelectedElementsWithRelated: (state, action: PayloadAction<string>) => {
      const stepId = action.payload;
      const [relatedIds] = findAllRelatedStepIds(stepId, state.workflowState);
      state.selectedElementIdToDelete = stepId;
      state.selectedElementIdsToDelete = relatedIds;
    },
    setStepAccordionStatus: (
      state,
      action: PayloadAction<{
        isOpen: boolean;
        moduleName: string;
        moduleVersion: string;
        order: number;
      }>
    ) => {
      return {
        ...state,
        stepModules: state.stepModules?.map((stepModule) => {
          if (
            stepModule.order === action.payload?.order &&
            stepModule.textId === action.payload?.moduleName &&
            stepModule.version === action.payload?.moduleVersion
          ) {
            return { ...stepModule, isOpen: action.payload.isOpen };
          }
          return { ...stepModule };
        }),
      };
    },
    setWorkflowState: (
      state,
      action: PayloadAction<IDesignOrchestrationSlice['workflowState']>
    ) => {
      return { ...state, workflowState: action.payload };
    },
    setXmlCode: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        xmlCode: action.payload,
      };
    },
    setXmlPanelWidth: (state, action: PayloadAction<number>) => {
      state.xmlPanelWidth = action.payload;
    },
    updateStepModuleField: (
      state,
      action: PayloadAction<{
        escSpecSymbols?: boolean;
        moduleName: string;
        moduleVersion: string;
        order?: number;
        parameterTextId: string;
        retryDelay?: number;
        retryDelayMultiplier?: number;
        retryNumber?: number;
        stepCondition?: string;
        stepId?: string;
        value: string;
      }>
    ) => {
      const {
        moduleName,
        moduleVersion,
        order,
        value,
        parameterTextId,
        escSpecSymbols,
        stepId,
        stepCondition,
        retryNumber,
        retryDelay,
        retryDelayMultiplier,
      } = action.payload;
      const moduleIndexToUpdate = state.stepModules.findIndex(
        (module) =>
          module.textId === moduleName &&
          module.version === moduleVersion &&
          module.order === order
      );

      if (state.stepModules[moduleIndexToUpdate]?.textId) {
        return {
          ...state,
          stepModules: state.stepModules.map((stepModule, moduleIndex) => {
            if (moduleIndex === moduleIndexToUpdate) {
              const stepLevelProperties: Partial<IStepModule> = {};

              includePropertiesInCleanStep(stepLevelProperties, {
                retryDelay,
                retryDelayMultiplier,
                retryNumber,
                stepCondition,
                stepId,
              });

              const updatedStepModule = {
                ...stepModule,
                ...stepLevelProperties,
              };

              if (parameterTextId) {
                const parameterIndexToUpdate = stepModule.parameters.findIndex(
                  (parameter) => parameter.textId === parameterTextId
                );

                if (parameterIndexToUpdate !== -1) {
                  updatedStepModule.parameters = stepModule.parameters.map(
                    (parameter, parameterIndex) => {
                      if (parameterIndex === parameterIndexToUpdate) {
                        if (escSpecSymbols === undefined) {
                          return { ...parameter, value };
                        } else {
                          return { ...parameter, escSpecSymbols };
                        }
                      }
                      return { ...parameter };
                    }
                  );
                }
              }

              return updatedStepModule;
            }
            return { ...stepModule };
          }),
        };
      }
      return { ...state };
    },
    updateViewState: (
      state,
      action: PayloadAction<Partial<IDesignOrchestrationSlice['viewState']>>
    ) => {
      state.viewState = {
        ...state.viewState,
        ...action.payload,
      };
    },
  },
});

export const selectOrchestrationDetails = (state: RootState) =>
  state.designOrchestration.orchestrationDetails;

export const selectAccordionStatus = (state: RootState) =>
  state.designOrchestration.isAccordionOpen;

export const selectXmlCode = (state: RootState) =>
  state.designOrchestration.xmlCode;

export const selectStepModules = (state: RootState) =>
  state.designOrchestration.stepModules;

export const selectStepModulesLength = (state: RootState) =>
  state.designOrchestration.stepModules?.length;

export const selectWorkflowState = (state: RootState) =>
  state.designOrchestration.workflowState;

export const selectXmlPanelWidth = (state: RootState) =>
  state.designOrchestration.xmlPanelWidth;

export const selectViewState = (state: RootState) =>
  state.designOrchestration.viewState;

export const selectSelectedElementIds = (state: RootState) =>
  state.designOrchestration.selectedElementIds;
export const selectSelectedElementIdsToDelete = (state: RootState) =>
  state.designOrchestration.selectedElementIdsToDelete;

export const {
  clearState,
  setOrchestrationDetails,
  setAccordionStatus,
  setXmlCode,
  updateStepModuleField,
  setStepAccordionStatus,
  setXmlPanelWidth,
  updateViewState,
  setSelectedElement,
  setSelectedElementsWithRelated,
  clearSelectedElements,
  addStepModuleAndUpdateWorkflow,
  deleteStepModuleAndUpdateWorkflow,
  setIsDeleteTooltipOpen,
  draggingStepModuleAndUpdateWorkflow,
  setWorkflowState,
} = designOrchestrationSlice.actions;

export default designOrchestrationSlice.reducer;
